import { useRef, type ChangeEvent, type ReactNode } from "react";
import AppButton, { type AppButtonProps } from "./AppButton";

type Props = Omit<AppButtonProps, "onChange" | "component" | "onClick"> & {
  accept?: string;
  multiple?: boolean;
  onFileSelected: (file: File) => void;
  onFilesSelected?: (files: FileList) => void;
  children: ReactNode;
};

/**
 * File picker triggered by a standard button.
 * Uses a programmatic input.click() — more reliable than label+hidden-input nesting
 * with MUI Button (busy wrappers / absolute clipping can block the native dialog).
 */
export default function AppFileButton({
  accept,
  multiple,
  onFileSelected,
  onFilesSelected,
  children,
  disabled,
  ...buttonProps
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) {
      if (multiple && onFilesSelected) {
        onFilesSelected(files);
      } else {
        const file = files[0];
        if (file) onFileSelected(file);
      }
    }
    e.target.value = "";
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={onChange}
        tabIndex={-1}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />
      <AppButton
        busyIgnore
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        {...buttonProps}
      >
        {children}
      </AppButton>
    </>
  );
}
